(() => {
  const selector = '.cl-pack img, .cl-product-card__image img, .cl-product-page__main-image, .cl-thumb img, .cl-cart-line__image img, .cl-menu-product__image img, .cl-social-product, .cl-video-card__poster--product';
  const cache = new Map();
  const queued = new WeakSet();
  const jobs = [];
  let active = 0;

  // Only remove neutral white connected to the outside of a studio packshot.
  // White areas enclosed by the bottle outline remain part of the product.
  const cutout = (source, preserveInteriors) => new Promise((resolve) => {
    const photo = new Image();
    const timeout = setTimeout(() => resolve(null), 15000);
    photo.crossOrigin = 'anonymous';
    photo.onload = () => {
      clearTimeout(timeout);
      try {
        const ratio = Math.min(1, 960 / Math.max(photo.naturalWidth, photo.naturalHeight));
        const width = Math.max(1, Math.round(photo.naturalWidth * ratio));
        const height = Math.max(1, Math.round(photo.naturalHeight * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return resolve(null);
        context.drawImage(photo, 0, 0, width, height);
        const pixels = context.getImageData(0, 0, width, height);
        const data = pixels.data;
        const neutralWhite = (index) => {
          const k = index * 4;
          const minimum = Math.min(data[k], data[k + 1], data[k + 2]);
          const maximum = Math.max(data[k], data[k + 1], data[k + 2]);
          return data[k + 3] > 250 && minimum >= 243 && maximum - minimum <= 18;
        };
        let whiteBorder = 0;
        let transparentBorder = 0;
        let borderCount = 0;
        const inspectBorder = (index) => { borderCount++; if (neutralWhite(index)) whiteBorder++; if (data[index * 4 + 3] < 250) transparentBorder++; };
        for (let x = 0; x < width; x++) { inspectBorder(x); inspectBorder((height - 1) * width + x); }
        for (let y = 1; y < height - 1; y++) { inspectBorder(y * width); inspectBorder(y * width + width - 1); }
        // Leave transparent photos and lifestyle photography untouched.
        if (transparentBorder / borderCount > .9) {
          // Normalize the transparent canvas margin as well, so packaging heights are comparable.
          let left = width, right = -1, top = height, bottom = -1;
          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              if (data[(y * width + x) * 4 + 3] <= 32) continue;
              left = Math.min(left, x); right = Math.max(right, x);
              top = Math.min(top, y); bottom = Math.max(bottom, y);
            }
          }
          if (right <= left || bottom <= top) return resolve({ transparent: true });
          if (left < 4 && top < 4 && width - right < 4 && height - bottom < 4) return resolve({ transparent: true });
          const result = document.createElement('canvas');
          result.width = right - left + 5;
          result.height = bottom - top + 5;
          result.getContext('2d').drawImage(canvas, left, top, right - left + 1, bottom - top + 1, 2, 2, right - left + 1, bottom - top + 1);
          return resolve(result.toDataURL('image/png'));
        }
        if (whiteBorder / borderCount < .9) return resolve(null);

        const removed = new Uint8Array(width * height);
        const queue = new Int32Array(width * height);
        let tail = 0;
        const add = (index) => {
          if (removed[index] || !neutralWhite(index)) return;
          removed[index] = 1;
          queue[tail++] = index;
        };
        for (let x = 0; x < width; x++) { add(x); add((height - 1) * width + x); }
        for (let y = 1; y < height - 1; y++) { add(y * width); add(y * width + width - 1); }
        for (let head = 0; head < tail; head++) {
          const index = queue[head];
          const x = index % width;
          if (x > 0) add(index - 1);
          if (x < width - 1) add(index + 1);
          if (index >= width) add(index - width);
          if (index < width * (height - 1)) add(index + width);
        }
        if (tail < width * height * .1 || tail > width * height * .98) return resolve(null);
        // Recover pale bottle interiors between the two visible edges of each row.
        // The exterior mask must never punch holes through an opaque white label.
        for (let y = 0; preserveInteriors && y < height; y++) {
          let start = width;
          let end = -1;
          for (let x = 0; x < width; x++) {
            const index = y * width + x;
            if (!removed[index] && data[index * 4 + 3] > 250) { start = Math.min(start, x); end = x; }
          }
          for (let x = start + 1; x < end; x++) removed[y * width + x] = 0;
        }
        let left = width, right = 0, top = height, bottom = 0;
        for (let index = 0; index < removed.length; index++) {
          const k = index * 4;
          if (removed[index]) { data[k + 3] = 0; continue; }
          const x = index % width;
          const y = Math.floor(index / width);
          const touchesBackground = (x > 0 && removed[index - 1]) || (x < width - 1 && removed[index + 1]) || (y > 0 && removed[index - width]) || (y < height - 1 && removed[index + width]);
          if (touchesBackground) {
            const minimum = Math.min(data[k], data[k + 1], data[k + 2]);
            const maximum = Math.max(data[k], data[k + 1], data[k + 2]);
            if (minimum > 218 && maximum - minimum < 22) data[k + 3] = Math.min(data[k + 3], Math.round((255 - minimum) * 255 / 37));
          }
          if (data[k + 3] > 32) {
            left = Math.min(left, x); right = Math.max(right, x);
            top = Math.min(top, y); bottom = Math.max(bottom, y);
          }
        }
        if (right <= left || bottom <= top) return resolve(null);
        context.putImageData(pixels, 0, 0);
        const result = document.createElement('canvas');
        const padding = 2;
        result.width = right - left + 1 + padding * 2;
        result.height = bottom - top + 1 + padding * 2;
        result.getContext('2d').drawImage(canvas, left, top, right - left + 1, bottom - top + 1, padding, padding, right - left + 1, bottom - top + 1);
        resolve(result.toDataURL('image/png'));
      } catch (_) {
        // Unsupported or cross-origin photos retain the original accessible image.
        resolve(null);
      }
    };
    photo.onerror = () => { clearTimeout(timeout); resolve(null); };
    photo.src = source;
  });

  const process = async (image) => {
    if (!image.isConnected) return;
    // Merchant campaign posters include their own typography, fruit and drink artwork.
    // Preserve the complete supplied creative instead of mistaking its cream backdrop for a white packshot.
    if (/\bpack\b|5\+1/i.test(image.alt)) {
      image.classList.add('cl-packshot-ready');
      return;
    }
    const originalSrc = image.getAttribute('src');
    const originalSrcset = image.getAttribute('srcset');
    if (!image.complete) {
      await new Promise(resolve => {
        image.addEventListener('load', resolve, {once: true});
        image.addEventListener('error', resolve, {once: true});
      });
    }
    const source = image.currentSrc || image.src;
    if (!source || !image.naturalWidth || source.startsWith('data:')) return;
    const preserveInteriors = !/\b(pack|caja)\b/i.test(image.alt);
    const cacheKey = `${source}|${preserveInteriors}`;
    if (!cache.has(cacheKey)) cache.set(cacheKey, cutout(source, preserveInteriors));
    const result = await cache.get(cacheKey);
    if (!result || !image.isConnected || image.getAttribute('src') !== originalSrc || image.getAttribute('srcset') !== originalSrcset) return;
    if (result.transparent) { image.classList.add('cl-packshot-ready'); return; }
    const replacement = new Image();
    replacement.src = result;
    await replacement.decode();
    if (!image.isConnected || image.getAttribute('src') !== originalSrc || image.getAttribute('srcset') !== originalSrcset) return;
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = result;
    image.classList.add('cl-packshot-ready');
  };
  const run = () => {
    while (active < 2 && jobs.length) {
      const image = jobs.shift();
      active++;
      process(image).catch(() => {}).finally(() => { active--; run(); });
    }
  };
  const enqueue = image => { jobs.push(image); run(); };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      enqueue(entry.target);
    });
  }, {rootMargin: '300px'}) : null;
  const initialize = (root = document) => {
    const images = [...root.querySelectorAll(selector)];
    if (root.matches?.(selector)) images.unshift(root);
    images.forEach(image => {
      if (queued.has(image)) return;
      queued.add(image);
      if (observer) observer.observe(image); else enqueue(image);
    });
  };
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  new MutationObserver(records => {
    const changed = new Set();
    records.forEach(record => {
      if (record.type === 'childList') {
        record.addedNodes.forEach(node => { if (node instanceof Element) initialize(node); });
        return;
      }
      const image = record.target;
      if (!(image instanceof HTMLImageElement) || !image.matches(selector) || image.src.startsWith('data:')) return;
      changed.add(image);
    });
    changed.forEach(image => {
      image.classList.remove('cl-packshot-ready');
      queued.delete(image);
      if (!queued.has(image)) {
        queued.add(image);
        if (observer) observer.observe(image); else enqueue(image);
      }
    });
  }).observe(document.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset']});
})();
